import { useStyleRegister } from '@ant-design/cssinjs';
import { ConfigProvider, Flex, theme } from 'antd';
import PropTypes from 'prop-types';
import { useContext, useLayoutEffect, useRef, useState } from 'react';

const EmbeddedAppFrame = ({ children, extra, fit = 'viewport', src, title }) => {
  const { csp } = useContext(ConfigProvider.ConfigContext);
  const { hashId, theme: tokenTheme, token } = theme.useToken();
  const container = useRef(null);
  const [top, setTop] = useState(0);

  useLayoutEffect(() => {
    if (fit === 'container') {
      return undefined;
    }
    const measure = () => setTop(container.current.getBoundingClientRect().top);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container.current.parentElement);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [fit]);

  useStyleRegister(
    {
      hashId,
      layer: { name: 'antd' },
      nonce: () => csp?.nonce,
      path: ['EmbeddedAppFrame', top],
      theme: tokenTheme,
      token,
    },
    () => ({
      '.kip-embedded-app': {
        position: 'relative',
        height: `calc(100dvh - ${top}px)`,
        minWidth: 0,
        overflow: 'hidden',
      },
      '.kip-embedded-app-extra': {
        position: 'absolute',
        top: token.marginXL,
        right: token.marginSM,
        zIndex: 1,
      },
      '.kip-embedded-app-container': { flex: 1, height: '100%', minHeight: 0 },
      '.kip-embedded-app-frame': { border: 0, display: 'block', height: '100%', width: '100%' },
      '.kip-embedded-app-state': { height: '100%', overflow: 'auto', padding: token.paddingLG },
    }),
  );

  return (
    <div
      ref={container}
      className={`kip-embedded-app ${fit === 'container' ? 'kip-embedded-app-container' : ''} ${hashId}`}
    >
      {extra && <div className={`kip-embedded-app-extra ${hashId}`}>{extra}</div>}
      {src ? (
        <iframe
          allow="clipboard-read; clipboard-write"
          className={`kip-embedded-app-frame ${hashId}`}
          src={src}
          title={title}
        />
      ) : (
        <Flex
          align="center"
          className={`kip-embedded-app-state ${hashId}`}
          gap="large"
          justify="center"
          vertical
        >
          {children}
        </Flex>
      )}
    </div>
  );
};

EmbeddedAppFrame.propTypes = {
  children: PropTypes.node,
  extra: PropTypes.node,
  fit: PropTypes.oneOf(['viewport', 'container']),
  src: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default EmbeddedAppFrame;
